import { useRef, useEffect, useMemo, useCallback } from 'react';
import gsap from 'gsap';

//* HOC's
import withUIContext from 'context/consumerHOC/UIConsumer';

//* Helpers
import { useActiveSection } from 'helpers';

//* Components
import Container from 'components/common/Container';
import Text from 'components/common/Text';
import Image from 'components/common/Image';

//* Style
import ProductDetailsStyle from './style';

const ProductDetails = ({ title, text, images, winWidth }) => {
	//! Refs
	const sectionRef = useRef();
	const imagesRef = useRef([]);
	const isSectionAnimated = useRef();

	//! State
	const activeSection = useActiveSection(sectionRef);

	//! Image border radius
	const imageBorderRadius = useMemo(() => `${winWidth <= 1024 ? 16 : 24}px`, [winWidth]);

	//! Animation
	const animate = useCallback(() => {
		gsap.to([imagesRef.current[0].children[0], imagesRef.current[2].children[0], imagesRef.current[1].children[0]], {
			borderRadius: `${imageBorderRadius}`,
			duration: 1.5,
			stagger: 0.3,
			delay: 0.2,
		});

		gsap.fromTo(
			[imagesRef.current[0], imagesRef.current[2], imagesRef.current[1]],
			{
				y: '150%',
			},
			{
				y: '0%',
				duration: 1,
				stagger: 0.3,
			}
		);
	}, [imageBorderRadius]);

	useEffect(() => {
		if (activeSection && !isSectionAnimated.current) {
			isSectionAnimated.current = true;
			animate();
		}
	}, [activeSection, animate]);

	useEffect(() => {
		if (isSectionAnimated.current) {
			animate();
		}
	}, [winWidth]);

	return (
		<ProductDetailsStyle ref={sectionRef}>
			<Container className='pd-container'>
				<div className='pd-info-container primary-color9'>
					<Text
						tag='h2'
						text={title}
						className='pd-title h5 minion3-medium uppercase'
					/>
					<Text
						text={text}
						className='pd-text notoSans-regular p p4'
					/>
				</div>

				<div className='pd-images-container'>
					{images.map((i, k) => (
						<div
							ref={(ref) => (imagesRef.current[k] = ref)}
							key={k}
							className='pd-image'>
							<Image
								src={i.src}
								alt={i.alt}
							/>
						</div>
					))}
				</div>
			</Container>
		</ProductDetailsStyle>
	);
};

export default withUIContext(ProductDetails, ['winWidth']);
